import { useEffect, useState } from 'react';
import { ErroApi, api } from '../api';
import { hojeIso, paraCampo, paraNumero, plural, porNome, rotulo } from '../formato';
import {
  Area, Aviso, Campo, Dialogo, ErroDaApi, Escolha, EscolhaPorCodigo, Rn, ZonaDeArquivo,
} from '../ui';
import type { Calibracao } from './Equipamento';

/**
 * Uma categoria do catálogo do parque (`0039`).
 *
 * `quantos` vem junto porque a tela de administração precisa saber
 * quantos equipamentos usam a categoria antes de aposentá-la — aposentar
 * uma categoria em uso não apaga nada, mas some do seletor, e quem faz
 * isso precisa ver o tamanho do que está mexendo.
 */
export interface Categoria {
  codigo: string; nome: string; papel: string;
  ativo: boolean; quantos: number;
  /** `adesivo` ou `gravado` — se a folha de etiquetas inclui (`0112`). */
  etiqueta?: string;
  /** Família do catálogo (`0096`): máquina, instrumento, item aferido… */
  familia?: string;
  /** Onda 210: o começo do código (CAP-) e se a folha pede identificação. */
  prefixo_codigo?: string | null;
  folha_identificacao?: boolean;
}

/**
 * Os quatro formulários da ficha do equipamento.
 *
 * Ficam num arquivo à parte porque a ficha já é longa e porque eles têm
 * uma coisa em comum que vale isolar: **nenhum deles calcula nada.** O
 * veredito da análise crítica, a aptidão do conjunto e o bloqueio da
 * baixa são todos do banco. O que estes formulários fazem é coletar o
 * que a pessoa sabe e mandar — e mostrar de volta o erro do servidor no
 * campo certo quando ele recusa.
 *
 * É a mesma divisão do resto do sistema: a tela ajuda a acertar, o banco
 * impede de errar.
 */

/**
 * Casca comum: título, erro do servidor, botões, estado de envio.
 *
 * Exportada desde a onda 88, quando a lista do parque passou a precisar
 * dela para o formulário de situação em lote. Copiá-la para lá teria
 * dado dois formulários que tratam erro de servidor de maneiras
 * diferentes — e o segundo a divergir seria o que ninguém confere.
 */
export function Form({
  titulo, aoFechar, enviar, children, rotuloEnviar = 'Gravar', dica,
}: {
  titulo: string;
  aoFechar(): void;
  enviar(): Promise<unknown>;
  children: React.ReactNode;
  rotuloEnviar?: string;
  dica?: React.ReactNode;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const submeter = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await enviar();
      aoFechar();
    } catch (x) {
      setErro(x);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialogo aberto titulo={titulo} aoFechar={aoFechar}>
      <form onSubmit={submeter}>
        {dica && <p className="sub">{dica}</p>}
        <ErroDaApi erro={erro} />
        {children}
        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="botao" disabled={enviando}>
            {enviando ? 'Gravando…' : rotuloEnviar}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/** O erro daquele campo, vindo do servidor. */
const doCampo = (erro: unknown, nome: string) =>
  (erro instanceof ErroApi ? erro.campo(nome) : undefined);

// ── 1. Certificado de calibração externa ──────────────────────────────

/**
 * O PDF é obrigatório aqui, e não "depois".
 *
 * Certificado registrado para anexar depois é o certificado que nunca é
 * anexado — e sem ele a análise crítica não pode ser aprovada
 * (`RN-155`). O registro ficaria pela metade num lugar onde meio
 * registro tem aparência de registro inteiro.
 */
export function FormCertificado({
  equipamento, aoFechar,
}: { equipamento: string; aoFechar(): void }) {
  const [v, setV] = useState({
    numeroCertificado: '',
    grandeza: '',
    fornecedorId: '',
    dataCalibracao: hojeIso(),
    dataValidade: '',
    faixaCalibrada: '',
    erro: '',
    incerteza: '',
    criterioAceitacao: '',
    resultado: 'aprovado',
    rastreabilidadePadroes: '',
  });
  const [pdf, setPdf] = useState<File | null>(null);

  /*
   * ── O que foi calibrado, e quem calibrou (`0114`) ─────────────────
   *
   * «Na janela de cadastrar novo certificado deveria aparecer um
   *  formulário para preenchimento, com o tipo de sensor/equipamento que
   *  está sendo calibrado (força, pressão, deslocamento, temperatura,
   *  velocidade, etc), qual o fornecedor da calibração» — Humberto,
   * 01/09/2026.
   *
   * A lista de fornecedores vem da rota `aptos`, e não filtrada aqui:
   * quem decide quem pode prestar serviço é a mesma visão que o gatilho
   * do banco consulta. Filtrar na tela seria a segunda cópia da regra.
   *
   * Ela reconsulta quando a grandeza muda, porque a aprovação é POR
   * ESCOPO: um fornecedor aprovado para força não está aprovado para
   * temperatura, e oferecê-lo ali seria oferecer um erro.
   */
  const [grandezas, setGrandezas] = useState<
    { codigo: string; nome: string; unidade_usual: string | null }[]>([]);
  const [fornecedores, setFornecedores] = useState<
    { id: string; razao_social: string; acreditacao: string | null }[]>([]);

  useEffect(() => {
    api<typeof grandezas>('/equipamentos/grandezas')
      .then(setGrandezas).catch(() => setGrandezas([]));
  }, []);

  useEffect(() => {
    const q = v.grandeza ? `?grandeza=${encodeURIComponent(v.grandeza)}` : '';
    api<typeof fornecedores>(`/fornecedores/aptos${q}`)
      .then(setFornecedores).catch(() => setFornecedores([]));
  }, [v.grandeza]);
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  const enviar = async () => {
    const fd = new FormData();
    Object.entries(v).forEach(([k, val]) => { if (val !== '') fd.append(k, val); });
    if (pdf) fd.append('certificado', pdf);
    return api(`/equipamentos/${equipamento}/calibracoes`, { formulario: fd });
  };

  return (
    <Form
      titulo="Registrar certificado de calibração"
      aoFechar={aoFechar}
      enviar={enviar}
      rotuloEnviar="Registrar"
      dica={(
        <>
          O certificado anterior <b>não é substituído</b>: ele fica no
          histórico do equipamento. O PDF é obrigatório porque sem ele a
          análise crítica não pode ser aprovada{' '}
          <Rn codigo="RN-155" diz="Análise crítica exige o certificado anexado." />.
        </>
      )}
    >
      <Campo rotulo="Número do certificado" required maxLength={120}
        {...campo('numeroCertificado')} />

      <div className="linha">
        <label className="campo">
          <span className="rotulo">O que foi calibrado</span>
          <select
            value={v.grandeza}
            required
            onChange={(e) => setV({ ...v, grandeza: e.target.value, fornecedorId: '' })}
          >
            <option value="">Escolha a grandeza…</option>
            {grandezas.map((g) => (
              <option key={g.codigo} value={g.codigo}>
                {g.nome}
                {g.unidade_usual ? ` (${g.unidade_usual})` : ''}
              </option>
            ))}
          </select>
          <span className="dica">
            A ficha agrupa por grandeza — é ela que responde «a força está
            calibrada hoje?».
          </span>
        </label>

        <label className="campo">
          <span className="rotulo">Quem calibrou</span>
          <select
            value={v.fornecedorId}
            required
            onChange={(e) => setV({ ...v, fornecedorId: e.target.value })}
          >
            <option value="">
              {v.grandeza ? 'Escolha o fornecedor…' : 'Escolha a grandeza primeiro'}
            </option>
            {fornecedores.map((f) => (
              <option key={f.id} value={f.id}>
                {f.razao_social}
                {f.acreditacao ? ` · ${f.acreditacao}` : ''}
              </option>
            ))}
          </select>
          <span className="dica">
            {v.grandeza && fornecedores.length === 0
              ? 'Nenhum fornecedor aprovado para esta grandeza. Cadastre e '
                + 'aprove em Fornecedores de calibração, na engrenagem.'
              : 'Só aparecem os avaliados e aprovados (17025 §6.6).'}
          </span>
        </label>
      </div>

      <div className="linha">
        <Campo rotulo="Data da calibração" type="date" required
          {...campo('dataCalibracao')} />
        <Campo rotulo="Válido até" type="date" required
          {...campo('dataValidade')} />
      </div>

      <Campo rotulo="Faixa calibrada" placeholder="0 a 50 kN"
        dica="A faixa que o certificado cobre — pode ser maior que a faixa de uso."
        {...campo('faixaCalibrada')} />

      <div className="linha">
        <Campo rotulo="Erro (opcional)" inputMode="decimal" {...campo('erro')} />
        <Campo rotulo="Incerteza (opcional)" inputMode="decimal"
          {...campo('incerteza')} />
      </div>

      <Escolha
        rotulo="Resultado do certificado"
        valor={v.resultado}
        opcoes={[
          { valor: 'aprovado', texto: 'Aprovado' },
          { valor: 'aprovado_com_restricao', texto: 'Aprovado com restrição' },
          { valor: 'reprovado', texto: 'Reprovado' },
        ]}
        limpavel={false}
        aoEscolher={(x) => x && setV({ ...v, resultado: x })}
      />

      <Area rotulo="Rastreabilidade dos padrões" rows={2}
        dica="Como o laboratório de calibração rastreia — RBC, INMETRO, padrão citado."
        {...campo('rastreabilidadePadroes')} />

      <ZonaDeArquivo
        rotulo="Certificado (PDF)"
        aceita="application/pdf"
        tipos="PDF"
        arquivos={pdf}
        textoVazio="Arraste o PDF do certificado aqui"
        dica="O arquivo entra imutável: emissão nova do laboratório é
          registro novo, nunca correção deste."
        aoEscolher={(fs) => setPdf(fs[0] ?? null)}
      />
    </Form>
  );
}

// ── 2. Análise crítica ────────────────────────────────────────────────

/**
 * Note o que este formulário NÃO tem: um campo "conforme".
 *
 * O veredito `|erro| + U ≤ EMA` é coluna calculada no banco. Foi um "Sim"
 * digitado à mão que deixou o cilindro 21 passar 0,015 mm abaixo do
 * mínimo da NBR 7182 (`D-21`); a decisão vale aqui igual.
 *
 * O que a pessoa preenche é o que só ela sabe: para que o equipamento vai
 * ser usado, qual o critério, e de ONDE vem o critério.
 */
export function FormAnaliseCritica({
  calibracao, aoFechar,
}: { calibracao: Calibracao; aoFechar(): void }) {
  const [v, setV] = useState({
    usoPretendido: '',
    faixaUsoMin: '', faixaUsoMax: '', faixaUsoUnidade: '',
    ema: '', emaUnidade: '', emaOrigem: '',
    maiorErro: calibracao.erro ?? '',
    incerteza: calibracao.incerteza ?? '',
    fatorAbrangencia: '2',
    regraDecisao: 'aceitacao_com_guarda',
    regraJustificativa: '',
    rastreabilidadeOk: 'sim',
    rastreabilidadeNota: calibracao.rastreabilidade_padroes ?? '',
    fatorCorrecao: '', correcaoAplicada: 'nao', correcaoOnde: '',
    conclusao: 'adequado', restricoes: '', observacoes: '',
  });
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  // A prévia do veredito, com a MESMA conta do banco. Não é ela que
  // decide — é para a pessoa ver o resultado antes de gravar e perceber
  // se digitou o EMA na unidade errada.
  const ema = paraNumero(v.ema);
  const err = paraNumero(v.maiorErro);
  const inc = paraNumero(v.incerteza);
  const comGuarda = v.regraDecisao === 'aceitacao_com_guarda';
  const previa = (ema !== undefined && err !== undefined && inc !== undefined)
    ? ema - (Math.abs(err) + (comGuarda ? inc : 0))
    : undefined;

  const enviar = () => api(
    `/equipamentos/calibracoes/${calibracao.id}/analise`,
    {
      corpo: {
        usoPretendido: v.usoPretendido,
        faixaUsoMin: paraNumero(v.faixaUsoMin),
        faixaUsoMax: paraNumero(v.faixaUsoMax),
        faixaUsoUnidade: v.faixaUsoUnidade || undefined,
        ema, emaUnidade: v.emaUnidade, emaOrigem: v.emaOrigem,
        maiorErro: err, incerteza: inc,
        fatorAbrangencia: paraNumero(v.fatorAbrangencia),
        regraDecisao: v.regraDecisao,
        regraJustificativa: v.regraJustificativa || undefined,
        rastreabilidadeOk: v.rastreabilidadeOk === 'sim',
        rastreabilidadeNota: v.rastreabilidadeNota || undefined,
        fatorCorrecao: paraNumero(v.fatorCorrecao),
        correcaoAplicada: v.correcaoAplicada === 'sim',
        correcaoOnde: v.correcaoOnde || undefined,
        conclusao: v.conclusao,
        restricoes: v.restricoes || undefined,
        observacoes: v.observacoes || undefined,
      },
    },
  );

  return (
    <Form
      titulo={`Análise crítica — ${calibracao.numero_certificado}`}
      aoFechar={aoFechar}
      enviar={enviar}
      rotuloEnviar="Registrar análise"
      dica={(
        <>
          A 17025 (6.4.6) não pede aprovação: pede que o laboratório
          <b> confirme que o resultado atende aos seus requisitos</b>. É
          isso que este formulário registra.
        </>
      )}
    >
      <Area rotulo="Uso pretendido" required rows={2}
        placeholder="Ensaio de ISC, penetração até 14 kN"
        dica="Para que este equipamento vai ser usado — é por isso que o erro é julgado."
        {...campo('usoPretendido')} />

      <div className="linha">
        <Campo rotulo="Faixa de uso — de" inputMode="decimal" {...campo('faixaUsoMin')} />
        <Campo rotulo="até" inputMode="decimal" {...campo('faixaUsoMax')} />
        <Campo rotulo="unidade" maxLength={20} {...campo('faixaUsoUnidade')} />
      </div>

      <div className="linha">
        <Campo rotulo="Erro máximo admissível" required inputMode="decimal"
          {...campo('ema')} />
        <Campo rotulo="unidade" required maxLength={20} {...campo('emaUnidade')} />
      </div>
      <Campo rotulo="De onde vem o critério" required maxLength={300}
        placeholder="NBR 9895:2016 — 1 % da carga"
        dica="Norma do ensaio ou procedimento interno. O certificado diz o erro que existe, não o que pode existir."
        {...campo('emaOrigem')} />

      <div className="linha">
        <Campo rotulo="Maior erro na faixa de uso" required inputMode="decimal"
          {...campo('maiorErro')} />
        <Campo rotulo="Incerteza U" required inputMode="decimal"
          {...campo('incerteza')} />
        <Campo rotulo="k" inputMode="decimal" {...campo('fatorAbrangencia')} />
      </div>

      <Escolha
        rotulo="Regra de decisão (7.1.3)"
        valor={v.regraDecisao}
        opcoes={[
          { valor: 'aceitacao_com_guarda', texto: 'Com banda de guarda (|erro| + U ≤ EMA)' },
          { valor: 'aceitacao_simples', texto: 'Aceitação simples (|erro| ≤ EMA)' },
        ]}
        limpavel={false}
        aoEscolher={(x) => x && setV({ ...v, regraDecisao: x })}
      />
      {!comGuarda && (
        <Area rotulo="Por que ignorar a incerteza?" required rows={2}
          dica="A aceitação simples é legítima em alguns casos e perigosa em todos os outros."
          {...campo('regraJustificativa')} />
      )}

      {previa !== undefined && (
        <Aviso tipo={previa >= 0 ? 'ok' : 'erro'}
          titulo={previa >= 0 ? 'Atende ao critério' : 'NÃO atende ao critério'}>
          Margem = {previa.toFixed(4).replace('.', ',')} {v.emaUnidade}.
          O veredito gravado é calculado pelo banco — esta prévia é só para
          você conferir a unidade antes de gravar.
        </Aviso>
      )}

      <Escolha
        rotulo="Rastreabilidade dos padrões conferida?"
        valor={v.rastreabilidadeOk}
        opcoes={[{ valor: 'sim', texto: 'Sim' }, { valor: 'nao', texto: 'Não' }]}
        limpavel={false}
        aoEscolher={(x) => x && setV({ ...v, rastreabilidadeOk: x })}
      />
      <Area rotulo="Nota sobre a rastreabilidade" rows={2}
        {...campo('rastreabilidadeNota')} />

      <div className="linha">
        <Campo rotulo="Fator de correção (se houver)" inputMode="decimal"
          {...campo('fatorCorrecao')} />
      </div>
      {v.fatorCorrecao !== '' && (
        <>
          <Escolha
            rotulo="O fator entra no cálculo do ensaio?"
            valor={v.correcaoAplicada}
            opcoes={[
              { valor: 'nao', texto: 'Não — apenas registrado' },
              { valor: 'sim', texto: 'Sim — aplicado' },
            ]}
            limpavel={false}
            aoEscolher={(x) => x && setV({ ...v, correcaoAplicada: x })}
          />
          {v.correcaoAplicada === 'sim' && (
            <Campo rotulo="Onde ele entra" required maxLength={300}
              dica="Sem isto o próximo técnico não tem como refazer a conta."
              {...campo('correcaoOnde')} />
          )}
        </>
      )}

      <Escolha
        rotulo="Conclusão"
        valor={v.conclusao}
        opcoes={[
          { valor: 'adequado', texto: 'Adequado ao uso' },
          { valor: 'adequado_com_restricao', texto: 'Adequado com restrição' },
          { valor: 'nao_adequado', texto: 'Não adequado' },
        ]}
        limpavel={false}
        aoEscolher={(x) => x && setV({ ...v, conclusao: x })}
      />
      {v.conclusao === 'adequado_com_restricao' && (
        <Area rotulo="Qual restrição" required rows={2}
          dica="Restrição em branco não restringe nada."
          {...campo('restricoes')} />
      )}

      <Area rotulo="Observações" rows={2}
        dica="Obrigatório se você concluir 'adequado' contra o resultado do cálculo."
        {...campo('observacoes')} />
    </Form>
  );
}

// ── 3. Sensor ─────────────────────────────────────────────────────────

export function FormSensor({
  equipamento, aoFechar,
}: { equipamento: string; aoFechar(): void }) {
  const [v, setV] = useState({
    codigoPatrimonial: '', categoria: 'celula_carga', nome: '',
    grandezaMedida: 'forca', posicao: '',
    fabricante: '', modelo: '', numeroSerie: '',
    capacidadeNominal: '', capacidadeUnidade: '',
    resolucao: '', resolucaoUnidade: '',
  });
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  const enviar = () => api(`/equipamentos/${equipamento}/componentes`, {
    corpo: {
      codigoPatrimonial: v.codigoPatrimonial,
      categoria: v.categoria,
      nome: v.nome,
      grandezaMedida: v.grandezaMedida,
      posicao: v.posicao || undefined,
      fabricante: v.fabricante || undefined,
      modelo: v.modelo || undefined,
      numeroSerie: v.numeroSerie || undefined,
      capacidadeNominal: paraNumero(v.capacidadeNominal),
      capacidadeUnidade: v.capacidadeUnidade || undefined,
      resolucao: paraNumero(v.resolucao),
      resolucaoUnidade: v.resolucaoUnidade || undefined,
      calibravel: true,
    },
  });

  return (
    <Form
      titulo="Cadastrar sensor"
      aoFechar={aoFechar}
      enviar={enviar}
      rotuloEnviar="Cadastrar"
      dica={(
        <>
          O sensor entra como registro próprio, com certificado e validade
          próprios — é o que permite à <Rn codigo="RN-158"
            diz="O conjunto vale pelo pior componente." /> reprovar o
          equipamento por causa dele.
        </>
      )}
    >
      <Campo rotulo="Código patrimonial" required maxLength={30}
        {...campo('codigoPatrimonial')} />
      <Campo rotulo="Nome" required maxLength={200}
        placeholder="Célula de carga 50 kN" {...campo('nome')} />

      <Escolha
        rotulo="Tipo"
        valor={v.categoria}
        opcoes={[
          { valor: 'celula_carga', texto: 'Célula de carga' },
          { valor: 'lvdt', texto: 'LVDT' },
          { valor: 'regua_potenciometrica', texto: 'Régua potenciométrica' },
          { valor: 'transdutor_pressao', texto: 'Transdutor de pressão' },
        ]}
        limpavel={false}
        aoEscolher={(x) => x && setV({
          ...v,
          categoria: x,
          grandezaMedida: x === 'celula_carga' ? 'forca'
            : x === 'transdutor_pressao' ? 'pressao' : 'deslocamento',
        })}
      />

      <Campo rotulo="Grandeza medida" required maxLength={60}
        {...campo('grandezaMedida')} />
      <Campo rotulo="Posição no equipamento" maxLength={120}
        placeholder="célula superior"
        dica="Dois sensores iguais na mesma prensa só se distinguem por isto."
        {...campo('posicao')} />

      <div className="linha">
        <Campo rotulo="Fabricante" maxLength={120} {...campo('fabricante')} />
        <Campo rotulo="Modelo" maxLength={120} {...campo('modelo')} />
      </div>
      <Campo rotulo="Número de série" maxLength={120} {...campo('numeroSerie')} />

      <div className="linha">
        <Campo rotulo="Capacidade" inputMode="decimal" {...campo('capacidadeNominal')} />
        <Campo rotulo="unidade" maxLength={20} {...campo('capacidadeUnidade')} />
      </div>
      <div className="linha">
        <Campo rotulo="Resolução" inputMode="decimal" {...campo('resolucao')} />
        <Campo rotulo="unidade" maxLength={20} {...campo('resolucaoUnidade')} />
      </div>
    </Form>
  );
}

// ── 4. Baixa ──────────────────────────────────────────────────────────

export function FormBaixa({
  equipamento, codigo, aoFechar,
}: { equipamento: string; codigo: string; aoFechar(): void }) {
  const [tipo, setTipo] = useState('inutilizado');
  const [motivo, setMotivo] = useState('');

  const enviar = () => api(`/equipamentos/${equipamento}/baixa`, {
    corpo: { tipo, motivo },
  });

  return (
    <Form
      titulo={`Dar baixa em ${codigo}`}
      aoFechar={aoFechar}
      enviar={enviar}
      rotuloEnviar="Dar baixa"
      dica={(
        <>
          O equipamento <b>não é apagado</b>: tudo que ele mediu continua no
          histórico e nos laudos já emitidos. O que a baixa impede é o
          futuro — calibração externa e interna novas passam a ser recusadas{' '}
          <Rn codigo="RN-159"
            diz="Equipamento com baixa não recebe calibração externa nem interna nova." />.
        </>
      )}
    >
      <Escolha
        rotulo="Motivo da saída"
        valor={tipo}
        opcoes={[
          { valor: 'inutilizado', texto: 'Inutilizado' },
          { valor: 'extraviado', texto: 'Extraviado' },
          { valor: 'substituido', texto: 'Substituído' },
          { valor: 'vendido', texto: 'Vendido' },
          { valor: 'descartado', texto: 'Descartado' },
        ]}
        limpavel={false}
        aoEscolher={(x) => x && setTipo(x)}
      />
      <Area
        rotulo="O que aconteceu"
        required rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        erro={motivo.length > 0 && motivo.length < 10
          ? 'descreva com um pouco mais de detalhe' : undefined}
        dica="O auditor vai ler isto. Mínimo de 10 caracteres."
      />
    </Form>
  );
}

// `doCampo` fica exportado para os formulários que vierem depois marcarem
// o campo exato que o servidor recusou, em vez de só mostrar o alerta.
export { doCampo };

// ── 5. Cadastro de equipamento ou padrão ──────────────────────────────

/**
 * O beco que este formulário fecha.
 *
 * O parque de equipamentos listava, filtrava e abria a ficha desde a S3,
 * e não tinha como cadastrar o primeiro equipamento. A API sabia criar
 * (`POST /equipamentos`); nenhuma tela chamava. O teste `26` encontrou
 * isso na primeira vez que rodou, junto com o beco do cliente.
 *
 * **Só o que identifica o equipamento é obrigatório** — código, categoria
 * e nome. Faixa, resolução e periodicidade entram depois, pela ficha.
 * Exigir tudo na criação é o desenho que produz cadastro inventado: quem
 * está com a balança na mão e não sabe a resolução digita qualquer coisa
 * para passar da tela, e o número errado fica com cara de número
 * conferido.
 *
 * **A categoria vem de catálogo fechado** (`0039`, decisão da Testesolo
 * em 23/08/2026). Era campo livre com sugestão; virou lista. O motivo é
 * o de sempre: campo livre produziu "balanca", "balança" e "Balanças"
 * como três categorias diferentes, e o filtro do parque passou a devolver
 * um terço do que devia. Categoria nova continua possível — só que por
 * cadastro, e só por administrador, que é o que a Testesolo pediu.
 */
export function FormEquipamento({
  papel, categorias, aoFechar, aoPronto,
}: {
  papel: 'equipamento' | 'padrao';
  categorias: Categoria[];
  aoFechar(): void;
  aoPronto(id: string): void;
}) {
  const ePadrao = papel === 'padrao';
  const [codigo, setCodigo] = useState('');
  const [categoria, setCategoria] = useState('');
  const [nome, setNome] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [modelo, setModelo] = useState('');
  const [serie, setSerie] = useState('');
  const [local, setLocal] = useState('');
  /* De onde veio — §14 do briefing, coluna criada na `0093`. */
  const [aquisicao, setAquisicao] = useState('');
  const [fabricacao, setFabricacao] = useState('');
  const [nf, setNf] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [erro, setErro] = useState<unknown>(null);

  return (
    <Form
      titulo={ePadrao ? 'Novo padrão de referência' : 'Novo equipamento'}
      aoFechar={aoFechar}
      rotuloEnviar="Cadastrar"
      dica={ePadrao
        ? 'O padrão entra sem certificado. Sem certificado válido ele não '
          + 'rastreia calibração interna nenhuma — anexe pela ficha, logo em seguida.'
        : 'Só o que identifica o equipamento é obrigatório aqui. Faixa, '
          + 'resolução e periodicidade de calibração entram pela ficha.'}
      enviar={async () => {
        setErro(null);
        /* A categoria agora é campo de digitar (352): sem o `required` do
           <select>, a falta é dita no campo antes de ir ao servidor. */
        if (!categoria) {
          const e = new ErroApi(400, 'Escolha a categoria.', [{ campo: 'categoria', erro: 'Escolha a categoria.' }]);
          setErro(e);
          throw e;
        }
        try {
          const r = await api<{ id: string }>('/equipamentos', {
            metodo: 'POST',
            corpo: {
              codigoPatrimonial: codigo.trim(),
              categoria: categoria.trim(),
              nome: nome.trim(),
              papel,
              fabricante: fabricante.trim() || undefined,
              modelo: modelo.trim() || undefined,
              numeroSerie: serie.trim() || undefined,
              localizacao: local.trim() || undefined,
              dataAquisicao: aquisicao || undefined,
              dataFabricacao: fabricacao || undefined,
              notaFiscal: nf.trim() || undefined,
              fornecedor: fornecedor.trim() || undefined,
            },
          });
          aoPronto(r.id);
          return r;
        } catch (e) { setErro(e); throw e; }
      }}
    >
      <Campo
        rotulo="Código patrimonial"
        value={codigo}
        onChange={(e) => setCodigo(e.currentTarget.value)}
        erro={doCampo(erro, 'codigoPatrimonial')}
        dica="É por ele que o ensaio cita o equipamento no laudo. Vai gravado em maiúsculas."
        autoFocus
        required
      />

      {/*
        * Digitando, e em ordem alfabética (onda 352, RN-273).
        *
        * «Ao adicionar novo equipamento, a ordem das categorias deveria ser
        *  em ordem alfabética. Deveria ter como digitar também e ir
        *  afunilando a categoria» — Humberto, 23/10/2026.
        *
        * Continua catálogo fechado (`0039`): o campo filtra enquanto se
        * digita, mas só aceita uma categoria da lista — sair com texto solto
        * devolve o que estava escolhido. É a peça das cápsulas
        * (`EscolhaPorCodigo`, P-UI-18): «bal» acha Balança e Balão
        * volumétrico; «cap» acha as duas Cápsulas.
        */}
      <EscolhaPorCodigo
        rotulo="Categoria"
        placeholder="digite para filtrar a categoria…"
        obrigatorio
        largo
        opcoes={[...categorias]
          .sort((a, b) => porNome(a.nome, b.nome))
          .map((c) => ({
            valor: c.codigo, codigo: c.nome,
            detalhe: c.familia ? (FAMILIA_NOME[c.familia] ?? rotulo(c.familia)) : undefined,
          }))}
        valor={categoria}
        aoEscolher={setCategoria}
        erro={doCampo(erro, 'categoria')}
        dica={categorias.length > 0
          ? 'Digite parte do nome para filtrar. A lista é o catálogo do parque; '
            + 'falta alguma? Quem administra o sistema cadastra — e ela passa a '
            + 'valer para todo mundo com o mesmo nome.'
          : 'Nenhuma categoria cadastrada ainda. Peça a quem '
            + 'administra o sistema para incluir as do parque.'}
      />

      <Campo
        rotulo="Nome"
        value={nome}
        onChange={(e) => setNome(e.currentTarget.value)}
        erro={doCampo(erro, 'nome')}
        dica="Como o laboratório chama. Ex.: Balança de precisão 0,01 g."
        required
      />
      <Campo
        rotulo="Fabricante"
        value={fabricante}
        onChange={(e) => setFabricante(e.currentTarget.value)}
        erro={doCampo(erro, 'fabricante')}
      />
      <Campo
        rotulo="Modelo"
        value={modelo}
        onChange={(e) => setModelo(e.currentTarget.value)}
        erro={doCampo(erro, 'modelo')}
      />
      <Campo
        rotulo="Número de série"
        value={serie}
        onChange={(e) => setSerie(e.currentTarget.value)}
        erro={doCampo(erro, 'numeroSerie')}
        dica="É o que amarra o equipamento ao certificado de calibração."
      />
      <Campo
        rotulo="Localização"
        value={local}
        onChange={(e) => setLocal(e.currentTarget.value)}
        erro={doCampo(erro, 'localizacao')}
        dica="Onde ele fica. Quem procura o equipamento procura por aqui."
      />

      {/*
        * A procedência.
        *
        * Não é burocracia: numa auditoria da 17025 a pergunta não é
        * quando o equipamento chegou, é COMO se sabe quando chegou. A
        * data sozinha é afirmação; com o número da nota, alguém confere.
        */}
      <Campo
        rotulo="Data de aquisição"
        type="date"
        value={aquisicao}
        onChange={(e) => setAquisicao(e.currentTarget.value)}
        erro={doCampo(erro, 'dataAquisicao')}
        dica="A data de emissão da nota fiscal de compra, não a de cadastro aqui."
      />
      {/*
        * A data de FABRICAÇÃO é outra pergunta (`0110`).
        *
        * Aquisição responde «desde quando é nosso»; fabricação responde
        * «que idade tem». Equipamento comprado usado tem aquisição de
        * ontem e fabricação de quinze anos atrás — e a idade é a primeira
        * pergunta quando um instrumento começa a reprovar na aferição.
        *
        * Opcional de verdade: boa parte do parque não tem essa data em
        * lugar nenhum, e um ano inventado é pior que campo em branco,
        * porque parece medido.
        */}
      {/*
        * Mês e ano, sem dia (`0117`).
        *
        * «O dia é muito difícil conseguir essa informação» — a plaqueta
        * diz «04/2019», e um campo de data cheio pediria um dia que
        * ninguém leu. O dia escrito no lugar do dia que não se sabe tem
        * a mesma cara do dia que se sabe: é o defeito da 0110 visto de
        * perto.
        */}
      <Campo
        rotulo="Mês de fabricação"
        type="month"
        value={fabricacao}
        onChange={(e) => setFabricacao(e.currentTarget.value)}
        erro={doCampo(erro, 'dataFabricacao')}
        dica="Opcional. Do manual ou da plaqueta do fabricante — deixe em
          branco se não souber; um ano chutado é pior que campo vazio."
      />
      {aquisicao && fabricacao && fabricacao > aquisicao.slice(0, 7) && (
        <Aviso tipo="atencao" titulo="A fabricação é posterior à aquisição">
          Confira as duas datas. Isto não impede gravar — as datas vêm de
          papéis diferentes, e uma nota fiscal com data errada não deve
          travar o cadastro de um equipamento que está na bancada.
        </Aviso>
      )}
      <Campo
        rotulo="Nota fiscal"
        value={nf}
        onChange={(e) => setNf(e.currentTarget.value)}
        erro={doCampo(erro, 'notaFiscal')}
        dica="O número da NF-e que comprova a compra."
      />
      <Campo
        rotulo="Fornecedor"
        value={fornecedor}
        onChange={(e) => setFornecedor(e.currentTarget.value)}
        erro={doCampo(erro, 'fornecedor')}
        dica="Razão social de quem vendeu."
      />

      <Aviso tipo="info" titulo="Ele nasce sem poder entrar em ensaio">
        Equipamento novo não tem calibração externa nem interna, e a <Rn codigo="RN-151" />{' '}
        não deixa ensaio escolher equipamento sem calibração interna válida. Registre o
        certificado — ou a verificação interna — pela ficha antes de usá-lo
        na bancada.
      </Aviso>
    </Form>
  );
}

// ── 6. Aprovação da análise crítica ───────────────────────────────────

/**
 * Aprovar é um ato, e um ato de outra pessoa.
 *
 * O formulário é curto de propósito: não há nada a digitar. Todo o
 * conteúdo técnico já foi escrito e conferido na análise, e o que
 * acontece aqui é a segunda assinatura que a 17025 pede — a decisão de
 * que aquele certificado serve para o uso pretendido.
 *
 * O que ele acrescenta é o peso: a aprovação carimba o certificado, e o
 * carimbo é o que libera o equipamento para entrar em ensaio. Quem clica
 * precisa saber que está liberando o equipamento, e não apenas
 * arquivando um documento.
 */
export function FormAprovarAnalise({
  analise, aoFechar, aoPronto,
}: {
  analise: { id: string; conforme: boolean; conclusao: string; restricoes: string | null };
  aoFechar(): void;
  aoPronto(): void;
}) {
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<unknown>(null);

  const naoAtende = !analise.conforme || analise.conclusao === 'inadequado';

  return (
    <Form
      titulo="Aprovar a análise crítica"
      aoFechar={aoFechar}
      rotuloEnviar="Aprovar"
      dica="A aprovação carimba o certificado e libera o equipamento para uso."
      enviar={async () => {
        setErro(null);
        try {
          const r = await api(`/equipamentos/analises/${analise.id}/aprovar`, {
            metodo: 'POST',
            corpo: { observacao: obs.trim() || undefined },
          });
          aoPronto();
          return r;
        } catch (e) { setErro(e); throw e; }
      }}
    >
      <ErroDaApi erro={erro} />

      {naoAtende && (
        <Aviso tipo="erro" titulo="Esta análise não concluiu pela adequação.">
          Aprovar aqui NÃO torna o equipamento adequado — a conclusão continua
          registrada como está, e a <Rn codigo="RN-151" /> continua valendo. Se
          o certificado não atende, o caminho é a baixa ou uma nova calibração,
          não a aprovação.
        </Aviso>
      )}

      {analise.restricoes && (
        <Aviso tipo="atencao" titulo="A análise registrou restrições de uso.">
          {analise.restricoes} — ao aprovar, você está aceitando o equipamento
          COM essas restrições.
        </Aviso>
      )}

      <Area
        rotulo="Observação da aprovação"
        value={obs}
        onChange={(e) => setObs(e.currentTarget.value)}
        rows={3}
        dica="Opcional. Fica na trilha de auditoria como motivo da aprovação."
      />

      <Aviso tipo="info" titulo="O que acontece ao aprovar">
        O certificado recebe o carimbo com o seu nome e a data, e o equipamento
        passa a ser oferecido nos ensaios cuja faixa de uso a análise cobre.
        A aprovação não se desfaz: se estiver errada, o caminho é uma análise
        nova sobre uma calibração nova.
      </Aviso>
    </Form>
  );
}

// ── O catálogo de categorias ─────────────────────────────────────────

/**
 * Administrar as categorias do parque (`0039`).
 *
 * Existe por uma frase da Testesolo: *"talvez existam mais categorias,
 * mas apenas administradores poderão cadastrá-las"*. Sem esta tela, a
 * segunda metade da frase viraria a primeira — a lista seria fechada
 * para todo mundo, inclusive para quem tem a permissão, e a única saída
 * seria uma migração nova a cada equipamento que o laboratório comprasse.
 *
 * **Aposentar não apaga.** Uma categoria em uso continua válida para o
 * equipamento que já a tem; o que muda é que ela some do seletor de quem
 * cadastra. Por isso a lista mostra QUANTOS equipamentos usam cada uma:
 * aposentar a categoria de trinta balanças é decisão diferente de
 * aposentar uma que ninguém usa, e quem decide precisa ver o número.
 */
/**
 * As cinco famílias do catálogo (`0096`) — a mesma lista do CHECK.
 *
 * Ficam aqui, e não numa consulta, porque são o vocabulário fechado que
 * a migração declarou: lista que muda por migração, não por cadastro.
 */
export const FAMILIAS_CAT: { codigo: string; nome: string; sub: string }[] = [
  { codigo: 'maquina', nome: 'Máquina de ensaio',
    sub: 'Aplica carga ou deslocamento e produz o ensaio.' },
  { codigo: 'instrumento', nome: 'Instrumento',
    sub: 'Mede, e tem calibração própria — balança, estufa, termômetro.' },
  { codigo: 'item_aferido', nome: 'Item calibrado internamente',
    sub: 'É medido pelos padrões e entrega uma constante ao cálculo — '
      + 'molde, cápsula, anel.' },
  { codigo: 'padrao', nome: 'Padrão de referência',
    sub: 'Afere os outros — paquímetro, trena, peso padrão.' },
  { codigo: 'componente', nome: 'Componente',
    sub: 'Célula, sensor ou régua que compõe uma máquina.' },
];

export const FAMILIA_NOME: Record<string, string> = Object.fromEntries(
  FAMILIAS_CAT.map((f) => [f.codigo, f.nome]));


/**
 * Corrigir a ficha do equipamento.
 *
 * «Não consigo preencher esses itens dos equipamentos, já vieram
 *  pré-preenchidos» — Humberto, 01/09/2026.
 *
 * Sete ondas carregaram 223 equipamentos por ferramenta, e a rota de
 * alteração só aceitava descrição — nome, fabricante, série, localização,
 * procedência. Capacidade, resolução, faixa e grandeza medida entravam no
 * cadastro e nunca mais saíam.
 *
 * O caminho que as pessoas tomam quando não há correção é sempre o mesmo:
 * apagar e cadastrar de novo. E isso levaria junto a trilha, as
 * calibrações e as aferições penduradas no registro.
 *
 * ── Por que o motivo é obrigatório aqui e não no resto ───────────────
 *
 * Resolução e faixa não são descrição: são o que a `RN-154` consulta para
 * decidir se o equipamento serve ao método. Mudar 0,01 para 0,1 numa
 * balança muda, retroativamente, o conjunto de ensaios que ela podia ter
 * feito — por isso o banco (`0102`) recusa a mudança sem motivo escrito,
 * e o formulário mostra antes quantos ensaios já se apoiam nela.
 */
export function FormCorrigirFicha({
  equipamento, alcance, aoFechar, aoPronto,
}: {
  equipamento: Record<string, unknown>;
  alcance?: { ensaios: number; calibracoes: number; afericoes: number } | null;
  aoFechar(): void;
  aoPronto(): void;
}) {
  const t = (c: string) => (equipamento[c] === null || equipamento[c] === undefined
    ? '' : String(equipamento[c]));

  const [codigo, setCodigo] = useState(t('codigo_patrimonial'));
  const [nome, setNome] = useState(t('nome'));
  const [fabricante, setFabricante] = useState(t('fabricante'));
  const [modelo, setModelo] = useState(t('modelo'));
  const [serie, setSerie] = useState(t('numero_serie'));
  const [local, setLocal] = useState(t('localizacao'));
  const [grandeza, setGrandeza] = useState(t('grandeza_medida'));
  /*
   * Número do banco entra no campo já escrito em português (`0117`).
   *
   * «O separador decimal usado em capacidade é o `.`, deveria ser `,`»
   * — Humberto, 01/09/2026. `numeric(12,2)` chega da API como a string
   * `"5000.00"`, e jogá-la direta no `<input>` mostra ao brasileiro um
   * ponto onde ele lê milhar: a capacidade de cinco mil gramas parecia
   * cinco. `paraCampo` devolve a notação de quem digita — vírgula
   * decimal, sem os zeros que o tipo da coluna carrega e ninguém
   * escreveu —, e `paraNumero` a traz de volta sem mudar de valor.
   *
   * O campo de digitação NÃO leva ponto de milhar, e isso é escolha:
   * `5.000` digitado num campo é ambíguo — o mesmo teclado escreve
   * `0.001` querendo um milésimo —, e adivinhar qual dos dois é o certo
   * é exatamente o erro que se está consertando. O milhar aparece onde
   * o número é LIDO: a ficha imprime `5.000 g` por `numero()`.
   */
  const n = (c: string) => paraCampo(equipamento[c]);

  const [cap, setCap] = useState(n('capacidade_nominal'));
  const [capU, setCapU] = useState(t('capacidade_unidade'));
  const [res, setRes] = useState(n('resolucao'));
  const [resU, setResU] = useState(t('resolucao_unidade'));
  const [fmin, setFmin] = useState(n('faixa_min'));
  const [fmax, setFmax] = useState(n('faixa_max'));
  const [fU, setFu] = useState(t('faixa_unidade'));
  const [periodo, setPeriodo] = useState(t('periodicidade_calibracao_meses'));
  const [origem, setOrigem] = useState(t('periodicidade_origem'));
  const [obs, setObs] = useState(t('observacoes'));
  /* Mês e ano — o dia não está na plaqueta (`0117`). */
  const mesFab = String(equipamento.data_fabricacao ?? '').slice(0, 7);
  const [fabricacao, setFabricacao] = useState(mesFab);
  const [motivo, setMotivo] = useState('');

  /** Vazio vira `undefined` (não mexe); número vira número. */
  const num = (v: string) => paraNumero(v);
  const txt = (v: string, antes: string) =>
    (v.trim() === antes.trim() ? undefined : v.trim());

  const mexeuNoCodigo = txt(codigo, t('codigo_patrimonial')) !== undefined;

  const mexeuNaMetrologia =
    mexeuNoCodigo
    || txt(grandeza, t('grandeza_medida')) !== undefined
    || txt(cap, n('capacidade_nominal')) !== undefined
    || txt(capU, t('capacidade_unidade')) !== undefined
    || txt(res, n('resolucao')) !== undefined
    || txt(resU, t('resolucao_unidade')) !== undefined
    || txt(fmin, n('faixa_min')) !== undefined
    || txt(fmax, n('faixa_max')) !== undefined
    || txt(fU, t('faixa_unidade')) !== undefined
    || txt(periodo, t('periodicidade_calibracao_meses')) !== undefined
    || txt(origem, t('periodicidade_origem')) !== undefined;

  return (
    <Form
      titulo="Corrigir a ficha"
      aoFechar={aoFechar}
      rotuloEnviar="Gravar a correção"
      dica={'Só o que você mudar é gravado. Cada alteração fica na trilha '
        + 'com autor, horário e motivo.'}
      enviar={async () => {
        await api(`/equipamentos/${String(equipamento.id)}`, {
          metodo: 'PATCH',
          corpo: {
            codigoPatrimonial: txt(codigo, t('codigo_patrimonial')),
            nome: txt(nome, t('nome')),
            fabricante: txt(fabricante, t('fabricante')),
            modelo: txt(modelo, t('modelo')),
            numeroSerie: txt(serie, t('numero_serie')),
            localizacao: txt(local, t('localizacao')),
            observacoes: txt(obs, t('observacoes')),
            dataFabricacao: txt(fabricacao, mesFab),
            grandezaMedida: txt(grandeza, t('grandeza_medida')),
            capacidadeNominal: txt(cap, n('capacidade_nominal')) === undefined
              ? undefined : num(cap),
            capacidadeUnidade: txt(capU, t('capacidade_unidade')),
            resolucao: txt(res, n('resolucao')) === undefined ? undefined : num(res),
            resolucaoUnidade: txt(resU, t('resolucao_unidade')),
            faixaMin: txt(fmin, n('faixa_min')) === undefined ? undefined : num(fmin),
            faixaMax: txt(fmax, n('faixa_max')) === undefined ? undefined : num(fmax),
            faixaUnidade: txt(fU, t('faixa_unidade')),
            periodicidadeCalibracaoMeses:
              txt(periodo, t('periodicidade_calibracao_meses')) === undefined
                ? undefined : Number(periodo),
            periodicidadeOrigem:
              txt(origem, t('periodicidade_origem')) === undefined
                ? undefined : origem,
            motivo: motivo.trim() || undefined,
          },
        });
        aoPronto();
      }}
    >
      {/*
        * O alcance vem ANTES dos campos, de propósito.
        *
        * Corrigir é permitido; corrigir sem saber o que já se apoia no
        * número é que não. Quem muda a resolução de uma balança que
        * assinou quarenta ensaios merece ver o quarenta antes de clicar.
        */}
      {alcance && (alcance.ensaios > 0 || alcance.afericoes > 0) && (
        <Aviso tipo="atencao" titulo="Já existe trabalho apoiado neste registro.">
          {plural(alcance.ensaios, 'ensaio', 'ensaios')},{' '}
          {plural(alcance.calibracoes, 'calibração externa', 'calibrações externas')} e{' '}
          {plural(alcance.afericoes, 'calibração interna', 'calibrações internas')} usam este equipamento. Faixa e
          resolução decidem a que método ele serve — corrigi-las muda, para
          trás, o que ele podia ter feito.
        </Aviso>
      )}

      {/*
        * O código, e o aviso que vem com ele.
        *
        * «As balanças teremos que renomear, pois é difícil lembrar o
        *  número de série» (Testesolo, 01/09/2026). Trocar é legítimo e
        * fica na trilha — mas o código é o que está gravado no objeto e
        * o que o QR da plaqueta carrega. Trocar aqui sem trocar na
        * bancada cria dois nomes para a mesma coisa, e quem ler a
        * plaqueta velha vai achar que o equipamento sumiu.
        */}
      <Campo
        rotulo="Código patrimonial"
        value={codigo}
        onChange={(e) => setCodigo(e.currentTarget.value)}
        maxLength={30}
        dica="É o que está gravado no objeto e o que o QR da plaqueta leva."
      />
      {mexeuNoCodigo && (
        <Aviso tipo="atencao" titulo="A plaqueta precisa ser reimpressa.">
          O QR carrega o código. Depois de gravar, imprima a etiqueta nova
          e troque a que está colada — enquanto as duas existirem, o mesmo
          equipamento tem dois nomes.
        </Aviso>
      )}

      <Campo rotulo="Nome" value={nome}
        onChange={(e) => setNome(e.currentTarget.value)} />

      <div className="linha">
        <Campo rotulo="Fabricante" value={fabricante}
          onChange={(e) => setFabricante(e.currentTarget.value)} />
        <Campo rotulo="Modelo" value={modelo}
          onChange={(e) => setModelo(e.currentTarget.value)} />
      </div>

      <div className="linha">
        <Campo rotulo="Número de série" value={serie}
          onChange={(e) => setSerie(e.currentTarget.value)} />
        <Campo rotulo="Localização" value={local}
          onChange={(e) => setLocal(e.currentTarget.value)}
          dica="Onde ele fica. Bancada, sala, armário." />
      </div>

      <h3 style={{ marginTop: '1rem' }}>Características metrológicas</h3>
      <p className="sub">
        São elas que decidem a que ensaio o equipamento serve. Mexer em
        qualquer uma exige motivo.
      </p>

      <Campo rotulo="Grandeza medida" value={grandeza}
        onChange={(e) => setGrandeza(e.currentTarget.value)}
        dica="massa, comprimento, temperatura, forca, deslocamento…" />

      <div className="linha">
        <Campo rotulo="Capacidade" value={cap} inputMode="decimal"
          onChange={(e) => setCap(e.currentTarget.value)} />
        <Campo rotulo="Unidade" value={capU}
          onChange={(e) => setCapU(e.currentTarget.value)} placeholder="g" />
      </div>

      <div className="linha">
        <Campo rotulo="Resolução" value={res} inputMode="decimal"
          onChange={(e) => setRes(e.currentTarget.value)} />
        <Campo rotulo="Unidade" value={resU}
          onChange={(e) => setResU(e.currentTarget.value)} placeholder="g" />
      </div>

      <div className="linha">
        <Campo rotulo="Faixa mínima" value={fmin} inputMode="decimal"
          onChange={(e) => setFmin(e.currentTarget.value)} />
        <Campo rotulo="Faixa máxima" value={fmax} inputMode="decimal"
          onChange={(e) => setFmax(e.currentTarget.value)} />
        <Campo rotulo="Unidade" value={fU}
          onChange={(e) => setFu(e.currentTarget.value)} placeholder="g" />
      </div>

      <div className="linha">
        <Campo rotulo="Periodicidade de calibração (meses)" value={periodo}
          inputMode="numeric"
          onChange={(e) => setPeriodo(e.currentTarget.value)}
          dica="Quantos meses até a próxima. É daqui que sai a data de vencimento." />
        {/*
          * De onde veio o intervalo (`0108`).
          *
          * O número decide o vencimento, e vencimento impede ensaio. Ele
          * entrou por três caminhos que a tela não distinguia — o
          * certificado, a planilha da casa e dedução do histórico —, e um
          * número deduzido com cara de número medido é pior que campo em
          * branco: campo em branco alguém preenche.
          *
          * Trocar «deduzida» por qualquer outra opção é o gesto que tira
          * o equipamento da fila. Por isso ele exige motivo como as
          * outras correções metrológicas: quem confirma um intervalo
          * está respondendo por ele.
          */}
        <Escolha
          rotulo="De onde veio esse intervalo"
          valor={origem}
          aoEscolher={(v) => setOrigem(v ?? '')}
          opcoes={[
            { valor: 'certificado', texto: 'O certificado diz' },
            { valor: 'norma', texto: 'A norma fixa' },
            { valor: 'fabricante', texto: 'O manual do fabricante' },
            { valor: 'politica_interna', texto: 'Decisão da Testesolo' },
            { valor: 'inferida', texto: 'Deduzida do histórico' },
          ]}
        />
      </div>

      <Campo
        rotulo="Mês de fabricação"
        type="month"
        value={fabricacao}
        onChange={(e) => setFabricacao(e.currentTarget.value)}
        dica="Opcional — mês e ano, como está na plaqueta. Costuma aparecer
          só quando alguém abre o manual." />

      <Area rotulo="Observações" value={obs} rows={3}
        onChange={(e) => setObs(e.currentTarget.value)} />

      <Campo
        rotulo={mexeuNaMetrologia ? 'Motivo da correção (obrigatório)' : 'Motivo'}
        value={motivo}
        onChange={(e) => setMotivo(e.currentTarget.value)}
        required={mexeuNaMetrologia}
        dica={mexeuNaMetrologia
          ? 'O auditor vai ler isto. Diga o que estava errado e de onde veio '
            + 'o número novo — «resolução conforme certificado MC 089/01/2025».'
          : 'Opcional enquanto só a descrição muda.'}
      />
    </Form>
  );
}

/**
 * Registrar um trecho de posse — de quem é o equipamento, e desde quando.
 *
 * Os regimes vêm da API, nunca de constante nesta tela: a lista fechada
 * mora no banco (`equipamento_regime`, `0093`), e uma segunda cópia aqui
 * é a mesma armadilha do `D-29`.
 */
export function FormPosse({
  equipamento, aoFechar,
}: { equipamento: string; aoFechar(): void }) {
  const [regimes, setRegimes] = useState<
    { codigo: string; rotulo: string; proprio: boolean }[]>([]);
  const [v, setV] = useState({
    regime: '',
    titular: '',
    documento: '',
    vigenciaInicio: hojeIso(),
    vigenciaFim: '',
    observacoes: '',
  });
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  useEffect(() => {
    api<{ codigo: string; rotulo: string; proprio: boolean }[]>(
      '/equipamentos/posse/regimes')
      .then(setRegimes)
      .catch(() => setRegimes([]));
  }, []);

  const proprio = regimes.find((r) => r.codigo === v.regime)?.proprio === true;

  const enviar = () => api(`/equipamentos/${equipamento}/posse`, {
    corpo: {
      regime: v.regime,
      titular: v.titular || undefined,
      documento: v.documento || undefined,
      vigenciaInicio: v.vigenciaInicio,
      vigenciaFim: v.vigenciaFim || undefined,
      observacoes: v.observacoes || undefined,
    },
  });

  return (
    <Form
      titulo="Registrar regime de posse"
      aoFechar={aoFechar}
      enviar={enviar}
      rotuloEnviar="Registrar"
      dica={(
        <>
          Um equipamento pode mudar de regime <b>sem sair do laboratório</b> —
          é o que aconteceu com as prensas MS 115 em 26/08/2026. O trecho
          anterior precisa terminar no dia em que este começa: dois regimes
          no mesmo dia são recusados pelo banco.
        </>
      )}
    >
      <Escolha
        rotulo="Regime"
        valor={v.regime}
        opcoes={regimes.map((r) => ({ valor: r.codigo, texto: r.rotulo }))}
        limpavel={false}
        aoEscolher={(x) => x && setV({ ...v, regime: x })}
      />

      <Campo
        rotulo={proprio ? 'Titular (dispensado — é a própria casa)' : 'Titular'}
        required={!proprio && v.regime !== ''}
        disabled={proprio}
        maxLength={200}
        placeholder="Owntec Soluções em Engenharia Ltda"
        dica="De quem é o equipamento. «Comodato de quem?» é a primeira pergunta da auditoria."
        {...campo('titular')} />

      <Campo rotulo="Documento" maxLength={200}
        placeholder="NF 1704 / contrato de locação 014-2026"
        dica="A nota ou o contrato que sustenta o regime. Sem ele, o regime é afirmação."
        {...campo('documento')} />

      <div className="linha">
        <Campo rotulo="Desde" type="date" required {...campo('vigenciaInicio')} />
        <Campo rotulo="Até (em branco = vigente)" type="date"
          {...campo('vigenciaFim')} />
      </div>

      <Area rotulo="Observações" rows={2} {...campo('observacoes')} />
    </Form>
  );
}

/**
 * Registrar uma versão de software ou firmware (onda 351, RN-272).
 *
 * Só inclusão: a troca é linha nova, e o engano se corrige registrando a
 * versão certa com o motivo — o banco recusa editar e apagar.
 */
export function FormSoftware({
  equipamento, aoFechar,
}: { equipamento: string; aoFechar(): void }) {
  const [v, setV] = useState({
    tipo: 'firmware', nome: '', versao: '', desde: hojeIso(), motivo: '',
  });
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });
  const enviar = () => api(`/equipamentos/${equipamento}/software`, {
    corpo: {
      tipo: v.tipo, nome: v.nome.trim() || undefined, versao: v.versao,
      desde: v.desde, motivo: v.motivo,
    },
  });
  return (
    <Form
      titulo="Registrar software ou firmware"
      aoFechar={aoFechar}
      enviar={enviar}
      rotuloEnviar="Registrar"
      dica={(
        <>
          A versão que estava no equipamento no dia do ensaio faz parte da
          medição (ISO/IEC 17025, 6.4.13). A troca é um registro novo: a
          versão anterior fica no histórico com o período em que valeu{' '}
          <Rn codigo="RN-272"
            diz="Software e firmware do equipamento são histórico: a troca é registro novo, nunca edição." />.
        </>
      )}
    >
      <Escolha
        rotulo="Tipo"
        valor={v.tipo}
        opcoes={[
          { valor: 'firmware', texto: 'Firmware (o programa dentro do equipamento)' },
          { valor: 'software', texto: 'Software (o programa no computador ligado a ele)' },
        ]}
        limpavel={false}
        aoEscolher={(x) => x && setV({ ...v, tipo: x })}
      />
      <Campo rotulo="Programa (em branco = o do próprio equipamento)" maxLength={80}
        placeholder="BluEvolution" {...campo('nome')} />
      <div className="linha">
        <Campo rotulo="Versão" required maxLength={60} placeholder="3.2.1"
          {...campo('versao')} />
        <Campo rotulo="Vale desde" type="date" required {...campo('desde')} />
      </div>
      <Area rotulo="Motivo" required rows={2}
        placeholder="Atualização do fabricante na visita de 02/09"
        dica="Instalação, atualização, correção do registro anterior…"
        {...campo('motivo')} />
    </Form>
  );
}
